import { useRecoilStateLoadable, useRecoilValue } from "recoil";
import { evidenceAtom, userAtom } from "@state/State";
import { IEvidence, IUser } from "@interfaces/Interfaces";
import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { DATA_REFRESH_INTERVAL } from "../../Constants";
import * as DatabaseService from "@controllers/DatabaseService";
import DataController from "@controllers/DataController";
import { useLoggedIn } from "@hooks/auth/useLoggedIn";

type RefreshUserEvidence = {
  refreshUserEvidence: (user: IUser) => Promise<IEvidence[]>;
};

const fetchUserEvidence = async (user: IUser): Promise<IEvidence[]> => {
  if (!user || !user.userData) {
    throw new Error("User is required");
  }

  const allEvidence = (await DatabaseService.getEvidence(user)) ?? [];
  return allEvidence;
};

export const useRefreshUserEvidence = (shouldLoad?: boolean): RefreshUserEvidence => {
  const user = useRecoilValue(userAtom);
  const [evidence, setEvidence] = useRecoilStateLoadable<IEvidence[] | null>(evidenceAtom);
  const loggedIn = useLoggedIn();
  const queryClient = useQueryClient();

  const isEnabled = Boolean(loggedIn && user && user.userData && shouldLoad);

  const { data: refreshedData } = useQuery({
    queryKey: ["user-evidence", user?.userData?.contactID],
    queryFn: () => fetchUserEvidence(user!),
    enabled: isEnabled,
    refetchInterval: isEnabled ? DATA_REFRESH_INTERVAL : false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!user || !user.userData) {
      queryClient.removeQueries({ queryKey: ["user-evidence"] });
    }
  }, [user, queryClient]);

  useEffect(() => {
    if (refreshedData && evidence.state === "hasValue") {
      const isEqual = _.isEqual(evidence.contents, refreshedData);

      if (!isEqual) {
        DataController.saveEvidence(refreshedData);
        setEvidence(refreshedData);
      }
    }
  }, [refreshedData, evidence, setEvidence]);

  const refreshUserEvidence = useCallback(
    async (_user: IUser): Promise<IEvidence[]> => {
      try {
        if (!_user || !_user.userData) {
          return [];
        }

        const data = await fetchUserEvidence(_user);

        if (evidence.state === "hasValue") {
          const isEqual = _.isEqual(evidence.contents, data);

          if (!isEqual) {
            await DataController.saveEvidence(data);
            setEvidence(data);
          }
        } else {
          await DataController.saveEvidence(data);
          setEvidence(data);
        }

        queryClient.invalidateQueries({ queryKey: ["user-evidence", _user.userData.contactID] });
        return data;
      } catch (error) {
        console.log(error);
        return [];
      }
    },
    [evidence, setEvidence, queryClient]
  );

  return {
    refreshUserEvidence,
  };
};
